import React, { useState, useRef, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { httpClient } from '../../../HttpClient';
import { uploadFileToBlob } from '../../../utils/uploadFileToBlob';
import { showStatusToast } from '../../../components/ui/Toast';
import "../Suivi.css";
import { CiFilter } from "react-icons/ci";
import { IoIosSearch } from "react-icons/io";
import { useEtatGlobal } from "../../EtatGlobal.js";
import { X, Mail, Paperclip, ChevronDown, ChevronUp } from "lucide-react";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import { useClient } from "../../Portefeuille/useClients.js";
import { missionCommencee, missionActive, estBloque, classeLigneBloquee } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";

import { dialog } from "../../../components/ui/Dialog";
const facultativeOptions = [
  "Entreprises implantées dans les zones suivantes (ZAFR, ZAIPME, ZFRR, ZUS, QPV, ZFU, ZFU-TE, ZRD, BER, ZFA DOM)",
  "Entreprises situées en Corse",
  "Médecins, auxiliaires médicaux et vétérinaires ruraux",
  "Jeunes entreprises innovantes (JEI) et les jeunes entreprises universitaires (JEU)",
  "Disquaires indépendants (avec pour activité principale la vente au détail de phonogrammes)",
  "Établissements de vente de livres neufs au détail possédant le label de librairie indépendante de référence",
  "Entreprises de spectacles vivants : théâtres nationaux, tournées théâtrales, concerts symphoniques, spectacles musicaux et variétés",
  "Caisses de crédit municipal",
];

const pleinDroitOptions = [
  "Location ou sous-location d’immeubles nus dont recettes brutes < 100 000 €",
  "Location ou sous-location d’immeubles meublés dont recettes brutes < 5 000 €",
  "Artisans et façonniers travaillant soit pour les particuliers avec des matériaux fournis, soit pour leur compte avec des matières leur appartenant, qu'ils aient ou non une enseigne ou une boutique, lorsqu'ils utilisent uniquement le concours d'apprenti(s) âgé(s) de 20 ans maximum au début de l'apprentissage. Ils peuvent se faire aider de leur conjoint, de leur partenaire Pacs et de leurs enfants.",
  "Chauffeurs de taxis ou d'ambulances, propriétaires ou locataires d'1 ou 2 voitures, de 7 places maximum (sans compter la place du chauffeur), qu'ils conduisent ou gèrent eux-mêmes, à condition que les 2 voitures ne soient pas en service en même temps et qu'ils respectent les tarifs réglementaires",
  "Vendeurs à domicile indépendants (VDI), pour leur rémunération brute totale inférieure à 7 772 €",
  "Coopératives et unions de sociétés coopératives d'artisans, sociétés coopératives et unions de sociétés coopératives de patrons bateliers, sociétés coopératives maritimes",
  "Grands ports maritimes, ports autonomes, ports gérés par des collectivités territoriales, des établissements publics ou des sociétés d'économie mixte (à l'exception des ports de plaisance)",
  "Certains pêcheurs, sociétés de pêche artisanale et inscrits maritimes",
  "Exploitants agricoles, certains groupements d'employeurs et certains GIE",
  "Certaines coopératives agricoles et leurs unions",
  "Établissements zoologiques pour leur activité agricole (soins donnés aux animaux ainsi que les spectacles et animations pédagogiques)",
  "Sociétés coopératives et participatives (Scop)",
  "Établissements privés d'enseignement du premier et second degré sous contrat avec l'État et d'enseignement supérieur sous convention ou reconnus d'utilité publique",
  "Professeurs de lettres, sciences et arts d'agrément et instituteurs primaires qui dispensent leur enseignement personnellement, soit à leur domicile ou au domicile de leurs élèves, soit dans un local dépourvu d'enseigne et ne comportant pas un aménagement spécial",
  "Peintres, sculpteurs, graveurs et dessinateurs considérés comme artistes et ne vendant que le produit de leur art. Cela inclut les graphistes à condition que leur activité soit limitée à la création d'œuvres graphiques.",
  "Photographes auteurs, pour leur activité relative à la réalisation de prises de vues et à la cession de leurs œuvres d'art ou de droits d'auteur",
  "Auteurs, compositeurs, chorégraphes, traducteurs percevant des droits d'auteur, et certaines catégories d'entrepreneurs de spectacles (spectacles vivants)",
  "Artistes lyriques et dramatiques",
  "Activités de presse : éditeurs de publications périodiques, services de presse en ligne, diffuseurs de presse spécialiste",
  "Sages-femmes et garde-malades (sauf s'ils relèvent de la profession d'infirmière)",
  "Avocats qui ont suivi le cursus de formation sanctionné par le CAPA, l'exonération est limitée à 2 ans à compter du début d'activité",
  "Médecins et auxiliaires de santé ouvrant un cabinet secondaire dans un désert médical ou dans une commune de moins de 2000 habitants",
  "Sportifs pour la seule pratique d'un sport",
  "Syndicats professionnels, quelle que soit leur forme juridique, et leurs unions pour leurs activités portant sur l'étude et la défense des droits et des intérêts collectifs matériels ou moraux de leurs membres ou des personnes qu'ils représentent",
  "Organismes HLM et les propriétaires ou locataires louant ou sous-louant une partie de leur habitation personnelle de façon occasionnelle à un prix raisonnable (ou en meublé, à condition que le logement constitue la résidence principale du sous-locataire)",
  "Exploitants de meublé de tourisme classé ou de chambre d'hôtes (sauf délibération contraire de la commune), à condition que ces locaux fassent partie de leur habitation personnelle (résidence principale ou secondaire en dehors des périodes de location) et ne constituent pas l'habitation principale ou secondaire du locataire",
  "Entreprises pour leur activité de production de biogaz, d'électricité et de chaleur par la méthanisation",
  "Activités à caractère social (sauf les mutuelles, leurs unions, et les institutions de prévoyance)",
  "Entreprises créées dans un bassin urbain à dynamiser (BUD) entre le 1er janvier 2018 et le 31 décembre 2022 et qui sont exonérées d'impôt sur le revenu ou d'impôt sur les sociétés. L'exonération est limitée à 7 ans à compter de la création.",
  "Entreprises implantées dans une zone de développement prioritaire (ZDP) entre le 1er janvier 2019 et le 31 décembre 2022 et qui sont exonérées d'impôt sur le revenu ou d'impôt sur les sociétés. L'exonération est limitée à 7 ans à compter de la création.",
  "Collectivités territoriales, établissements publics et organismes de l'État",
];

function SuiviCFE({ suiviCFEData, loading, refetch }) {
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [currentClientId, setCurrentClientId] = useState(null);
  const [currentSuivi, setCurrentSuivi] = useState(null);
  const [currentClient, setCurrentClient] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);

  const [filteredFacultative, setFilteredFacultative] = useState([]);
  const [showFacultative, setShowFacultative] = useState(false);

  const [filteredPlein, setFilteredPlein] = useState([]);
  const [showPlein, setShowPlein] = useState(false);

  const [showAllPlein, setShowAllPlein] = useState(false);
  const [showAllFacultative, setShowAllFacultative] = useState(false);

  // Fiche client — même cache React Query partagé que Portefeuille.js
  // (["client", id] / ["clients"]) au lieu d'un fetch + cache local isolé :
  // ainsi une édition faite ici (via Update.js) se reflète instantanément
  // dans Portefeuille.js et vice-versa.
  const queryClient = useQueryClient();
  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const { data: ficheClientDetail, isLoading: ficheClientLoading } =
    useClient(selectedClientId);

  const showClientDetails = (client) => {
    setSelectedClientId(client.id);
    setFicheClientOpen(true);
  };

  // En attendant que useClient(selectedClientId) réponde, on affiche tout de
  // suite le snapshot déjà embarqué dans suiviCFEData (même principe que
  // Portefeuille.js : détail React Query prioritaire, fallback liste).
  const ficheClientFallback =
    suiviCFEData.find((c) => c.id === selectedClientId) || null;
  const ficheClientData = ficheClientDetail || ficheClientFallback;

  // Propage une édition (nom, groupement, collaborateur...) vers le cache
  // React Query partagé — même mécanique que handleUpdateUserInList dans
  // Portefeuille.js.
  const handleUpdateClientInList = (updatedClient) => {
    queryClient.setQueryData(["client", updatedClient.id], updatedClient);
    queryClient.setQueryData(["clients"], (prev) =>
      Array.isArray(prev)
        ? prev.map((c) => (c.id === updatedClient.id ? updatedClient : c))
        : prev
    );
  };

  // Facultative
  const handleFacultativeChange = (e) => {
    const value = e.target.value;
    setCurrentSuivi({ ...currentSuivi, facultativeExoneration: value });

    if (value.length > 0) {
      const filtered = facultativeOptions.filter((option) =>
        option.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredFacultative(filtered);
      setShowFacultative(true);
    } else {
      setFilteredFacultative([]);
      setShowFacultative(false);
    }
  };

  const handleFacultativeClick = (option) => {
    setCurrentSuivi({ ...currentSuivi, facultativeExoneration: option });
    setShowFacultative(false);
  };

  // Plein droit
  const handlePleinChange = (e) => {
    const value = e.target.value;
    setCurrentSuivi({ ...currentSuivi, pleinExoneration: value });

    if (value.length > 0) {
      const filtered = pleinDroitOptions.filter((option) =>
        option.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredPlein(filtered);
      setShowPlein(true);
    } else {
      setFilteredPlein([]);
      setShowPlein(false);
    }
  };

  const handlePleinClick = (option) => {
    setCurrentSuivi({ ...currentSuivi, pleinExoneration: option });
    setShowPlein(false);
  };

  const { utilisateur, patchSuiviCFERecord } = useEtatGlobal();

  const [invoiceData, setInvoiceData] = useState({
    fromEmail: "",
    toEmails: "",
    ccEmails: "",
    subject: "",
    message: "",
  });

  // quand on clique sur "Créer une facture"
  const toggleInvoiceForm = () => {
    const clientName = currentClient.nom || "";
    const montant = currentSuivi.montantTaxe || "0";
    const annees = selectedYear || "2025";

    setInvoiceData({
      fromEmail: `${utilisateur.email}`,
      toEmails: `${currentClient.email}`,
      ccEmails: "",
      subject: `CFE`,
      message: `  Bonjour,
  
  Nous vous informons que la CFE de l’année ${annees} pour ${clientName} s’élève à ${montant} €.
  
  Aurions-nous votre accord pour procéder au télépaiement ?
  
  Restant dans l'attente de votre décision,
  
  Bien cordialement`, // <-- version texte
    });
    setShowInvoiceForm(!showInvoiceForm);
  };

  // soumission

  const [loadingEmailSend, setLoadingEmailSend] = useState(false);
  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    setLoadingEmailSend(true);

    // Transformer le texte en HTML (chaque \n → <p>…</p>)
    const htmlMessage = invoiceData.message
      .split("\n")
      .map((line) => (line.trim() ? `<p>${line}</p>` : ""))
      .join("");

    try {
      // --- 1️⃣ Envoi de l'email ---
      await httpClient.post("https://backend-myalfa.vercel.app/api/email-reel", {
        fromEmail: invoiceData.fromEmail,
        toEmails: invoiceData.toEmails
          ? invoiceData.toEmails.split(",").map((email) => email.trim())
          : [],
        ccEmails: invoiceData.ccEmails
          ? invoiceData.ccEmails.split(",").map((email) => email.trim())
          : [],
        subject: invoiceData.subject,
        message: htmlMessage,
      });

      dialog.alert("Facture envoyée avec succès !");

      // ✅ Fermer immédiatement après l’email
      setLoadingEmailSend(false);
      setShowInvoiceForm(false);
      setInvoiceData({
        fromEmail: "",
        toEmails: "",
        ccEmails: "",
        subject: "",
        message: "",
      });

      // 🔄 Mise à jour du statut en arrière-plan
      if (currentClientId) {
        httpClient.put(
          `https://backend-myalfa.vercel.app/api/suiviCFE/${currentClientId}`,
          { statutInformationClient: "validée" }
        )
          .then(() => console.log("Statut ‘statutInformationClient’ mis à jour en ‘validée’"))
          .catch((err) => console.error("Erreur update statut:", err))
          .finally(() => refetch());
      } else {
        refetch();
      }
    } catch (error) {
      console.error("Erreur lors de l’envoi de la facture :", error);
      dialog.alert("Impossible d’envoyer la facture.");
      setLoadingEmailSend(false);
      setShowInvoiceForm(false);
    }
  };

  // Date filter state
  const [selectedYear, setSelectedYear] = useState("2025");
  // Filter overlay states
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);
  const overlayRef = useRef(null);

  // Multi-filters: array of { column, selectedValues[] }
  const [filterRows, setFilterRows] = useState([
    { column: "statutInformationClient", selectedValues: [] },
  ]);

  const [searchTerm, setSearchTerm] = useState("");

  // Close overlay on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (overlayRef.current && !overlayRef.current.contains(e.target)) {
        setShowFilterOverlay(false);
      }
    };
    if (showFilterOverlay)
      document.addEventListener("mousedown", handleClickOutside);
    else document.removeEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showFilterOverlay]);

  // Unique values for each filterable column & years
  // Unique values for each filterable column & years
  const uniqueValues = useMemo(() => {
    const cols = [
      "statutInformationClient",
      "statutAccordClient",
      "statutPaiement",
    ];
    const dict = {};

    // --- Génération des valeurs uniques pour les colonnes existantes
    cols.forEach((col) => {
      const all = suiviCFEData
        .flatMap((client) => client.suiviCFE || [])
        .map((s) => s[col]);
      dict[col] = Array.from(new Set(all.filter(Boolean)));
    });

    // --- Génération dynamique de la liste d'années
    const currentYear = new Date().getFullYear(); // ex : 2025
    const maxYear = currentYear + 2; // va jusqu'à 2 ans plus tard (ex : 2027)

    // Récupère les années présentes dans les données
    const years = Array.from(
      new Set(
        suiviCFEData
          .map((client) => new Date(client.dateCloture).getFullYear())
          .filter((year) => !isNaN(year))
      )
    );

    // Ajoute dynamiquement les années jusqu'à maxYear
    for (let y = currentYear; y <= maxYear; y++) {
      if (!years.includes(y)) years.push(y);
    }

    // Trie les années et convertit en chaînes
    years.sort((a, b) => a - b);
    dict["Année clôture"] = years.map(String);

    return dict;
  }, [suiviCFEData]);

  // Handlers for filter management
  const handleAddFilter = () => {
    setFilterRows((prev) => [
      ...prev,
      { column: "statutInformationClient", selectedValues: [] },
    ]);
  };

  const handleRemoveFilter = (idx) => {
    setFilterRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFilterChange = (idx, field, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) =>
        i === idx ? { ...f, [field]: value, selectedValues: [] } : f
      )
    );
  };

  const handleCheckboxValueChange = (idx, val) => {
    setFilterRows((prev) =>
      prev.map((f, i) => {
        if (i !== idx) return f;
        const sel = Array.isArray(f.selectedValues) ? f.selectedValues : [];
        const updated = sel.includes(val)
          ? sel.filter((v) => v !== val)
          : [...sel, val];
        return { ...f, selectedValues: updated };
      })
    );
  };

  // Apply filters + date filter to data
  const filteredData = suiviCFEData
    // ✅ Filtre par année
    .filter((client) => {
      if (selectedYear) {
        const yr = new Date(client.dateCloture).getFullYear();
        const selected = parseInt(selectedYear, 10);
        // Garde tous les clients dont la dateCloture est <= année sélectionnée
        return yr <= selected;
      }
      return true; // si aucune année sélectionnée, on garde tout
    })
    // Mission pas encore débutée cette année-là → masqué ; client archivé
    // depuis une année antérieure → masqué aussi.
    .filter(
      (client) =>
        !selectedYear ||
        (missionCommencee(client, selectedYear) &&
          missionActive(client, selectedYear))
    )
    // ✅ Filtre par nom
    .filter((client) => {
      if (!searchTerm) return true;
      return client.nom
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase().trim());
    })
    // ✅ Filtres avancés existants
    .filter((client) => {
      const rows =
        client.suiviCFE && client.suiviCFE.length ? client.suiviCFE : [{}];
      return rows.some((suivi) =>
        filterRows.every(({ column, selectedValues }) => {
          if (!selectedValues.length) return true;
          const v = suivi[column];
          return selectedValues.includes(v);
        })
      );
    });

  // Edit handlers
  const handleEditClick = (client, suivi) => {
    setCurrentClient(client);
    setCurrentClientId(client.id);
    setCurrentSuivi(suivi);
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSuivi((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async () => {
    setIsSubmitting(true);
    setShowModal(false);

    const patch = {
      montantTaxe: currentSuivi.montantTaxe || "",
      pleinExoneration: currentSuivi.pleinExoneration || "",
      facultativeExoneration: currentSuivi.facultativeExoneration || "",
      montantvan1: currentSuivi.montantvan1 || "",
      montantcfen1: currentSuivi.montantcfen1 || "",
      montantcvaen1: currentSuivi.montantcvaen1 || "",
      statutInformationClient: currentSuivi.statutInformationClient || "",
      statutAccordClient: currentSuivi.statutAccordClient || "",
      statutPaiement: currentSuivi.statutPaiement || "",
      commentaire: currentSuivi.commentaire || "",
    };

    // Optimistic : la ligne du tableau reflète tout de suite les nouvelles
    // valeurs sans attendre la réponse serveur ni le refetch qui suit.
    patchSuiviCFERecord(currentClientId, patch);

    try {
      const payload = { ...patch };

      // Si un document a été sélectionné — upload direct navigateur → Blob
      // (contourne la limite 4,5 Mo), un toast informe qu'un envoi de
      // fichier — parfois long selon sa taille — est en cours.
      if (currentSuivi.DocumentFile) {
        const toast = showStatusToast({ status: "loading", titre: "Envoi du document...", message: "0 %" });
        payload.documentUrl = await uploadFileToBlob(
          currentSuivi.DocumentFile,
          "https://backend-myalfa.vercel.app/api/suiviCFE/upload-token",
          ({ percentage }) =>
            toast.update({ status: "loading", titre: "Envoi du document...", message: `${Math.round(percentage)} %` })
        );
        toast.update({ status: "success", titre: "Document envoyé", message: "" });
      }

      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/suiviCFE/${currentClientId}`,
        payload
      );
    } catch (err) {
      console.error("Erreur update SuiviCFE", err);
    } finally {
      setIsSubmitting(false);
      refetch();
    }
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
          <div className="h-9 w-64 rounded-md bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-5">
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#6e1a36]">
                {["Dossier", "Info Client", "Accord Client", "Paiement", "Commentaire", "Pièce jointe"].map((col, i) => (
                  <th key={i} className="px-3 py-3.5 text-left">
                    <div className="h-3 rounded bg-[#ffffff33] animate-pulse" style={{ width: "70%" }} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  {["45%", "18%", "18%", "15%", "60%", "20px"].map((w, j) => (
                    <td key={j} className="px-3 py-3">
                      <div className="h-3 rounded bg-slate-200 animate-pulse" style={{ width: w }} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Barre de recherche + année + filtre */}
      <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrer par nom du dossier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 w-full rounded-md bg-slate-50 border border-slate-200 text-sm focus:bg-white focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-5">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="border border-slate-300 rounded-md
                                    h-9 px-2 text-xs leading-tight w-20
                                    bg-white"
          >
            <option value="">Toutes</option>
            {uniqueValues["Année clôture"].map((yr) => (
              <option key={yr} value={yr}>
                {yr}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowFilterOverlay(!showFilterOverlay)}
            className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
          >
            <CiFilter className="w-3 h-3" />
            Filtrer
          </button>
        </div>
        {showFilterOverlay && (
          <div
            ref={overlayRef}
            className="fixed top-[40%] left-[65%] -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between mb-2">
              <p className="font-semibold">Filtres avancés</p>
              <button
                className="text-sm text-slate-600 hover:text-slate-800"
                onClick={() => {
                  setFilterRows([
                    { column: "statutInformationClient", selectedValues: [] },
                  ]);
                  setSelectedYear("");
                }}
              >
                Réinitialiser
              </button>
            </div>
            {filterRows.map((f, idx) => (
              <div key={idx} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <select
                    value={f.column}
                    onChange={(e) =>
                      handleFilterChange(idx, "column", e.target.value)
                    }
                    className="border border-slate-200 rounded-md p-1 text-sm"
                  >
                    {Object.keys(uniqueValues).map((col) => {
                      const labelMap = {
                        statutInformationClient: "Info Client",
                        statutAccordClient: "Accord Client",
                        statutPaiement: "Paiement",
                        "Année clôture": "Année clôture",
                      };
                      return (
                        <option key={col} value={col}>
                          {labelMap[col] || col}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => handleRemoveFilter(idx)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    - Supprimer
                  </button>
                </div>
                <div className="flex flex-col ml-2">
                  {uniqueValues[f.column]?.map((val) => (
                    <label key={val}>
                      <input
                        type="checkbox"
                        checked={f.selectedValues.includes(val)}
                        onChange={() => handleCheckboxValueChange(idx, val)}
                        className="mr-2"
                      />
                      {val || "(vide)"}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button
              className="text-sm text-slate-700 hover:text-slate-900 mt-2"
              onClick={handleAddFilter}
            >
              + Ajouter filtre
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 250px)", minHeight: "420px" }}
        >
          <table className="w-full" style={{ fontSize: "0.72rem" }}>
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#6e1a36] text-xs">
                <th className="px-2 py-3.5 text-white font-semibold text-left">
                  Dossier
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Info Client
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Accord Client
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Paiement
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-left">
                  Commentaire
                </th>
                <th className="px-2 py-3.5 text-white font-semibold text-center">
                  Pièce jointe
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.map((client) => {
                const rows =
                  client.suiviCFE && client.suiviCFE.length
                    ? client.suiviCFE
                    : [{}];
                return rows.map((suivi, i) => (
                  <tr
                    key={`${client.id}-${i}`}
                    className={classeLigneBloquee(client)}
                    style={{
                      backgroundColor:
                        suivi.statutPaiement === "Payé"
                          ? "#e9f6ec"
                          : suivi.statutPaiement === "non réglé"
                          ? "#fff8dc"
                          : "transparent",
                    }}
                  >
                    <td
                      onClick={() => handleEditClick(client, suivi)}
                      className="px-2 py-2 font-medium text-slate-900 cursor-pointer"
                    >
                      {i === 0 && (
                        <>
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              showClientDetails(client);
                            }}
                            className="hover:text-[#9B1B5D] hover:underline transition-colors"
                          >
                            {client.nom}
                          </span>
                          {estBloque(client) && <BadgeBloque />}
                        </>
                      )}
                    </td>
                    <td
                      onClick={() => handleEditClick(client, suivi)}
                      className="px-2 py-1.5 text-center cursor-pointer"
                    >
                      {suivi.statutInformationClient && (
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-medium leading-tight ${
                            suivi.statutInformationClient === "validée"
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                              : "bg-amber-100 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {suivi.statutInformationClient}
                        </span>
                      )}
                    </td>
                    <td
                      onClick={() => handleEditClick(client, suivi)}
                      className="px-2 py-1.5 text-center cursor-pointer"
                    >
                      <div className="flex justify-center">
                        {suivi.statutAccordClient === "validée" ? (
                          <div className="w-3 h-3 rounded-full bg-emerald-500 shadow shadow-emerald-500/50" />
                        ) : (
                          <div className="w-3 h-3 rounded-full bg-slate-200" />
                        )}
                      </div>
                    </td>
                    <td
                      onClick={() => handleEditClick(client, suivi)}
                      className="px-2 py-1.5 text-center cursor-pointer"
                    >
                      {suivi.statutPaiement && (
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[9px] font-medium leading-tight ${
                            suivi.statutPaiement === "Payé"
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                              : "bg-amber-100 text-amber-700 border border-amber-200"
                          }`}
                        >
                          {suivi.statutPaiement}
                        </span>
                      )}
                    </td>
                    <td
                      onClick={() => handleEditClick(client, suivi)}
                      className="px-2 py-1.5 text-slate-600 cursor-pointer"
                    >
                      <span
                        className="line-clamp-1"
                        title={suivi.commentaire || ""}
                      >
                        {suivi.commentaire || ""}
                      </span>
                    </td>
                    <td
                      onClick={() => handleEditClick(client, suivi)}
                      className="px-2 py-1.5 text-center cursor-pointer"
                    >
                      {suivi.Document && (
                        <a
                          href={suivi.Document}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex text-slate-500 hover:text-[#9B1B5D] transition-colors"
                        >
                          <Paperclip className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </td>
                  </tr>
                ));
              })}

              {filteredData.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center py-12 text-sm text-slate-500"
                  >
                    Aucun client trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && currentSuivi && (
        <div className="overlayAC">
          <div className="inoverlayMS">
            <button
              aria-label="Fermer"
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              style={{ zIndex: 10000 }}
            >
              <X className="w-4 h-4" />
            </button>
            <div className="w-[85%] mx-auto mt-5">
              <div className="border-b-4 border-[#8B1538] pb-4 mb-6">
                <h2 className="text-xl font-bold text-slate-900 m-0">
                  Détails CFE — {currentClient?.nom}
                </h2>
                <p className="text-xs text-slate-500 mt-1 m-0">
                  Année {selectedYear}
                </p>
              </div>

              {/* --- Upload Document --- */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Pièce jointe Avis CFE
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    name="Document"
                    onChange={(e) =>
                      setCurrentSuivi((prev) => ({
                        ...prev,
                        DocumentFile: e.target.files[0],
                      }))
                    }
                    className="text-sm text-slate-600 file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs file:font-medium hover:file:bg-slate-200"
                  />
                  {currentSuivi.Document && (
                    <a
                      href={currentSuivi.Document}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#9B1B5D] hover:underline"
                    >
                      <Paperclip className="w-3.5 h-3.5" />
                      Pièce jointe actuelle
                    </a>
                  )}
                </div>
              </div>
                            {/* --- Champs ajoutés --- */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Montant de la taxe
                </label>
                <input
                  placeholder="XX,XX"
                  className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                  name="montantTaxe"
                  value={currentSuivi.montantTaxe || ""}
                  onChange={handleInputChange}
                />
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-5">
                <h4 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                  Vérification exonération
                </h4>

                {/* Exonérations de plein droit */}
                <div className="mb-4 relative">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Exonérations de plein droit
                  </label>
                  <div className="flex items-center gap-3">
                    <textarea
                      className="flex-1 rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all resize-none"
                      name="pleinExoneration"
                      value={currentSuivi.pleinExoneration || ""}
                      onChange={handlePleinChange}
                      onFocus={() =>
                        currentSuivi.pleinExoneration && setShowPlein(true)
                      }
                      onBlur={() => setTimeout(() => setShowPlein(false), 150)}
                      rows={3}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAllPlein((prev) => !prev)}
                      className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 transition flex-shrink-0"
                    >
                      {showAllPlein ? (
                        <ChevronUp className="w-3.5 h-3.5 text-slate-600" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-slate-600" />
                      )}
                    </button>
                  </div>

                  {/* Liste filtrée si on tape */}
                  {showPlein && filteredPlein.length > 0 && !showAllPlein && (
                    <ul className="absolute top-[70px] left-0 w-[calc(100%-3rem)] max-h-[150px] overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-10 divide-y divide-slate-100 m-0 p-0 list-none">
                      {filteredPlein.map((option, index) => (
                        <li
                          key={index}
                          onClick={() => handlePleinClick(option)}
                          className="px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer"
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Liste complète quand on clique sur la flèche */}
                  {showAllPlein && (
                    <ul className="absolute top-[70px] left-0 w-[calc(100%-3rem)] max-h-[200px] overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-10 divide-y divide-slate-100 m-0 p-0 list-none">
                      {pleinDroitOptions.map((option, index) => (
                        <li
                          key={index}
                          onClick={() => {
                            handlePleinClick(option);
                            setShowAllPlein(false);
                          }}
                          className="px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer"
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Exonérations facultatives */}
                <div className="relative">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Exonérations facultatives
                  </label>
                  <div className="flex items-center gap-3">
                    <textarea
                      className="flex-1 rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all resize-none"
                      name="facultativeExoneration"
                      value={currentSuivi.facultativeExoneration || ""}
                      onChange={handleFacultativeChange}
                      onFocus={() =>
                        currentSuivi.facultativeExoneration &&
                        setShowFacultative(true)
                      }
                      onBlur={() =>
                        setTimeout(() => setShowFacultative(false), 150)
                      }
                      rows={3}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAllFacultative((prev) => !prev)}
                      className="p-2 rounded-md border border-slate-300 hover:bg-slate-100 transition flex-shrink-0"
                    >
                      {showAllFacultative ? (
                        <ChevronUp className="w-3.5 h-3.5 text-slate-600" />
                      ) : (
                        <ChevronDown className="w-3.5 h-3.5 text-slate-600" />
                      )}
                    </button>
                  </div>

                  {/* Liste filtrée quand on tape */}
                  {showFacultative &&
                    filteredFacultative.length > 0 &&
                    !showAllFacultative && (
                      <ul className="absolute top-[70px] left-0 w-[calc(100%-3rem)] max-h-[150px] overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-10 divide-y divide-slate-100 m-0 p-0 list-none">
                        {filteredFacultative.map((option, index) => (
                          <li
                            key={index}
                            onClick={() => handleFacultativeClick(option)}
                            className="px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer"
                          >
                            {option}
                          </li>
                        ))}
                      </ul>
                    )}

                  {/* Liste complète quand on clique sur la flèche */}
                  {showAllFacultative && (
                    <ul className="absolute top-[70px] left-0 w-[calc(100%-3rem)] max-h-[200px] overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg z-10 divide-y divide-slate-100 m-0 p-0 list-none">
                      {facultativeOptions.map((option, index) => (
                        <li
                          key={index}
                          onClick={() => {
                            handleFacultativeClick(option);
                            setShowAllFacultative(false);
                          }}
                          className="px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 cursor-pointer"
                        >
                          {option}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-5">
                <h4 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                  Vérification plafonnement de la CET N-1
                </h4>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Montant de la VA N-1
                  </label>
                  <input
                    className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    name="montantvan1"
                    value={currentSuivi.montantvan1 || ""}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Montant de la CFE N-1
                  </label>
                  <input
                    className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    name="montantcfen1"
                    value={currentSuivi.montantcfen1 || ""}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Montant de la CVAE N-1
                  </label>
                  <input
                    className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                    name="montantcvaen1"
                    value={currentSuivi.montantcvaen1 || ""}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Montant du dégrèvement à demander
                  </label>
                  <p className="text-sm font-bold text-[#9B1B5D] m-0">
                    {(() => {
                      const va = parseFloat(currentSuivi.montantvan1) || 0;
                      const cfe = parseFloat(currentSuivi.montantcfen1) || 0;
                      const cvae = parseFloat(currentSuivi.montantcvaen1) || 0;

                      let montant = cfe + cvae - 0.01438 * va;

                      // Empêche le montant d'être négatif
                      montant = Math.max(montant, 0);

                      return isNaN(montant)
                        ? "—"
                        : `${montant.toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} €`;
                    })()}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between gap-4 mb-5">
                <p className="text-sm text-slate-700 m-0">
                  Demander l'accord de paiement
                </p>
                <button
                  onClick={toggleInvoiceForm}
                  disabled={!currentSuivi.montantTaxe}
                  className={`px-4 py-1.5 rounded-md text-white transition ${
                    !currentSuivi.montantTaxe
                      ? "bg-slate-400 cursor-not-allowed"
                      : "bg-[#840040] hover:bg-[#6e1a36]"
                  }`}
                >
                  <Mail className="w-4 h-4" />
                </button>
              </div>

              {showInvoiceForm && (
                <form
                  onSubmit={handleInvoiceSubmit}
                  className="mt-5 p-5 bg-slate-50 border border-slate-200 rounded-xl mb-5"
                >
                  <h3 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                    Envoyer une demande d'accord de paiement par mail
                  </h3>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      De
                    </label>
                    <input
                      type="email"
                      value={invoiceData.fromEmail}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          fromEmail: e.target.value,
                        })
                      }
                      required
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Pour (séparés par virgule)
                    </label>
                    <input
                      type="text"
                      value={invoiceData.toEmails}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          toEmails: e.target.value,
                        })
                      }
                      required
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      CC (séparés par virgule)
                    </label>
                    <input
                      type="text"
                      value={invoiceData.ccEmails}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          ccEmails: e.target.value,
                        })
                      }
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Objet
                    </label>
                    <input
                      type="text"
                      value={invoiceData.subject}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          subject: e.target.value,
                        })
                      }
                      required
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Message
                    </label>
                    <textarea
                      rows={10}
                      value={invoiceData.message}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          message: e.target.value,
                        })
                      }
                      required
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loadingEmailSend}
                      className={`px-6 h-10 rounded-md font-semibold text-sm text-white transition ${
                        loadingEmailSend
                          ? "bg-slate-400 cursor-not-allowed"
                          : "bg-emerald-600 hover:bg-emerald-700"
                      }`}
                    >
                      {loadingEmailSend
                        ? "En cours d'envoi..."
                        : "Envoyer le Mail"}
                    </button>
                  </div>
                </form>
              )}

              {/* --- Champs existants --- */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Statut Info Client
                </label>
                <select
                  className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                  name="statutInformationClient"
                  value={currentSuivi.statutInformationClient || ""}
                  onChange={handleInputChange}
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="validée">validée</option>
                  <option value="en attente">en attente</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Statut Accord Client
                </label>
                <select
                  className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                  name="statutAccordClient"
                  value={currentSuivi.statutAccordClient || ""}
                  onChange={handleInputChange}
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="validée">validée</option>
                  <option value="en attente">en attente</option>
                </select>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Statut Paiement
                </label>
                <select
                  className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                  name="statutPaiement"
                  value={currentSuivi.statutPaiement || ""}
                  onChange={handleInputChange}
                >
                  <option value="">-- Sélectionner --</option>
                  <option value="Payé">Payé</option>
                  <option value="non réglé">non réglé</option>
                </select>
              </div>

              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Commentaire
                </label>
                <textarea
                  name="commentaire"
                  value={currentSuivi.commentaire || ""}
                  onChange={handleInputChange}
                  rows={6}
                  className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                />
              </div>

              <div className="flex justify-end gap-3 mb-10">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-5 h-10 rounded-md font-medium text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 transition"
                >
                  Annuler
                </button>
                <button
                  onClick={handleUpdate}
                  className="px-5 h-10 rounded-md font-semibold text-sm bg-[#840040] hover:bg-[#6e1a36] text-white"
                >
                  {isSubmitting ? "En attente..." : "Enregistrer"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={handleUpdateClientInList}
      />
    </div>
  );
}

export default SuiviCFE;
